import {FC, useCallback, useEffect, useMemo, useRef, useState, CSSProperties} from 'react'
import {FaChevronRight} from 'react-icons/fa'
import {KTSVG} from '../../../../_metronic/helpers'
import {Link, useLocation, useNavigate, useParams} from 'react-router-dom'
import ReportStep, {ReportStepRef} from './ReportStep'
import {useAuth} from '../../auth'
import {OP} from '../core/operationPermissions'
import {useOperationsAccess} from '../core/useOperationsAccess'
import {useOperationsScopeUx} from '../core/operationsScopeUx'
import {
  getAssignmentById,
  listReportAttachments,
  uploadReportAttachment,
  submitReport,
  updatedSubmittedReport,
  submitConsumptionROB,
  getCreatedReportById,
  getVoyageList,
} from '../core/_requests'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import {
  CreatedReports,
  Fields,
  Reports,
  ReportStatus,
  Submenu,
  Voyage,
  WarningLevel,
} from '../core/_models'
import CloseReportModal from './CloseReportModal'
import ConfirmOverwriteModal from './ConfirmOverwriteModal'
import {validateField} from '../core/validationUtils'
import {toast, ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import AddConsumptionROB, {AddConsumptionROBRef, ConsumptionRequestDTO} from './AddConsumptionROB'
import BunkerReportStep, {PendingReportAttachment} from './BunkerReportStep'
import {
  BUNKER_FIELD_LABEL_LOOKUP,
  BUNKER_REQUIRED_ATTACHMENT_DOCUMENT_NAME,
} from './bunkerReportSchema'
import { isFuelChangeoverReportName, ensureFuelChangeoverPresetFields } from './fuelChangeoverReportSchema'
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage'
import {formatReportDateTimeDisplay, isReportDateTimeLabel} from '../core/reportDateTime'
import {resolveAssignmentTemplate, sortFieldsByOrder} from '../core/reportStructure'
import {
  getPositionReportActionState,
  normalizePositionReportStatus,
} from '../core/positionReportWorkflow'
import { applyCiiSummaryCalculations } from '../core/ciiSummaryUtils'
import { applyPositionReportCalculations } from '../core/positionReportCalculations'

const EMPTY_TAB_VALUES: Record<string, any> = {}
const assignmentDetailsCache = new Map<number, any>()

const CreateReport: FC = () => {
  const {assignmentId} = useParams<{assignmentId: string}>()

  const [selectedReport, setSelectedReport] = useState<Partial<Reports> | null>(null)
  const [tabs, setTabs] = useState<Submenu[]>([])
  const [fields, setFields] = useState<Fields[]>([])
  const [isCloseReportModalOpen, setIsCloseReportModalOpen] = useState<boolean>(false)
  const [isConfirmOverWriteModalOpen, setIsConfirmOverWriteModalOpen] = useState<boolean>(false)

  const stepRefs = useRef<ReportStepRef[]>([])
  const [stepCompletionStatus, setStepCompletionStatus] = useState<boolean[]>([])

  const [hasWarnings, setHasWarnings] = useState<boolean>(false)
  const [totalWarnings, setTotalWarnings] = useState<number>(0)
  const [warningsByStep, setWarningsByStep] = useState<Record<number, number>>({})
  const [hasWarningsByStep, setHasWarningsByStep] = useState<Record<number, boolean>>({})
  const [errorsByStep, setErrorsByStep] = useState<Record<number, number>>({})
  const [hasErrorsByStep, setHasErrorsByStep] = useState<Record<number, boolean>>({})
  const [modalActionType, setModalActionType] = useState<'draft' | 'submit'>('draft')
  const [warningFields, setWarningFields] = useState<
    {stepId: number; fieldId: number; message: string}[]
  >([])
  const [errorFields, setErrorFields] = useState<
    {stepId: number; stepName: string; fieldId: number; fieldLabel: string; message: string}[]
  >([])

  const navigate = useNavigate()
  const [reportDate, setReportDate] = useState<string>(() => new Date().toISOString())
  const [currentStep, setCurrentStep] = useState(0)
  const step = tabs[currentStep]
  const [isLoadingFields, setIsLoadingFields] = useState(false)
  const [isReportActionInProgress, setIsReportActionInProgress] = useState(false)
  const [isSubmitActionConsumed, setIsSubmitActionConsumed] = useState(false)
  const reportActionInFlightRef = useRef(false)
  const submitActionConsumedRef = useRef(false)
  const location = useLocation()

  const consumptionRobRef = useRef<AddConsumptionROBRef>(null)

  const authCtx: any = useAuth()
  const {currentUser} = authCtx
  const {opCan, isSuperAdmin} = useOperationsAccess()
  const scopeUx = useOperationsScopeUx(isSuperAdmin)
  const canWriteReport =
    opCan(OP.CREATE_POSITION_REPORT) || opCan(OP.UPDATE_POSITION_REPORT)

  const token =
    authCtx?.auth?.auth?.jwt ||
    authCtx?.auth?.jwt ||
    sessionStorage.getItem('jwt') ||
    sessionStorage.getItem('token') ||
    ''

  interface LocationState {
    selectedTabId?: number
    selectedVoyage?: Voyage
    selectedLegId?: number
    selectedLeg?: any
    reportId?: number
    pendingBunkerAttachments?: PendingReportAttachment[]
    consumptionPreviewData?: ConsumptionRequestDTO | null
    formData?: {
      [tabId: number]: {
        [id: number]: {
          label: string
          value: any
        }
      }
    }
    draftReport?: CreatedReports
    isEditMode?: boolean
  }

  const state = (location.state || {}) as LocationState

  const {
    selectedTabId,
    selectedVoyage,
    selectedLegId,
    selectedLeg,
    draftReport,
    pendingBunkerAttachments: passedPendingBunkerAttachments,
    consumptionPreviewData: passedConsumptionPreviewData,
    formData: passedFormData,
    reportId: passedReportId,
    isEditMode: initialIsEditMode,
  } = state

  const [voyage, setVoyage] = useState<Voyage | null>(selectedVoyage ?? null)
  const [isEditMode, setIsEditMode] = useState<boolean>(initialIsEditMode ?? false)
  const [draftReportData, setDraftReportData] = useState<CreatedReports | null>(draftReport ?? null)
  const [activeReportId, setActiveReportId] = useState<number | null>(
    Number.isFinite(Number(passedReportId)) && Number(passedReportId) > 0
      ? Number(passedReportId)
      : draftReport?.id ?? null
  )
  const isEditModeActive = isEditMode || Boolean(draftReportData?.id)
  const [isCheckingActiveDraft, setIsCheckingActiveDraft] = useState<boolean>(
    !isEditModeActive && !draftReportData
  )
  const normalizeSavedStatus = normalizePositionReportStatus

  const DRAFT_BACKUP_TTL_MS = 24 * 60 * 60 * 1000

  // ── Real-Time Auto-Save State for Position Report ──
  const [autoSaveStatus, setAutoSaveStatus] = useState<'IDLE' | 'SAVING' | 'SAVED' | 'OFFLINE' | 'ERROR'>('IDLE')
  const [lastAutoSavedTime, setLastAutoSavedTime] = useState<Date | null>(null)
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null)
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null)
  const activeReportIdRef = useRef<number | null>(activeReportId)
  const isAutoSavingRef = useRef<boolean>(false)
  const isFormDirtyRef = useRef<boolean>(false)
  const performAutoSaveDraftRef = useRef<((latestFormData?: any, latestConsumption?: any) => Promise<void>) | null>(null)

  const [pendingLocalDraft, setPendingLocalDraft] = useState<{
    formData: any
    consumptionPreviewData: any
    savedAt: string
  } | null>(null)

  const backupStorageKey = useMemo(() => {
    const vesselKey =
      voyage?.vessel?.id ??
      (voyage as any)?.vesselId ??
      draftReportData?.vesselDto?.id ??
      (draftReportData as any)?.vesselId ??
      currentUser?.vessel?.id ??
      0
    const voyageKey = voyage?.id || selectedVoyage?.id || 0
    const userKey = currentUser?.id ?? (currentUser as any)?.userId ?? 'anon'
    return `shipsedge_pr_backup_u${userKey}_v${vesselKey}_a${assignmentId}_vy${voyageKey}`
  }, [
    assignmentId,
    currentUser?.id,
    (currentUser as any)?.userId,
    currentUser?.vessel?.id,
    draftReportData?.vesselDto?.id,
    (draftReportData as any)?.vesselId,
    selectedVoyage?.id,
    voyage,
  ])

  useEffect(() => {
    activeReportIdRef.current = activeReportId
  }, [activeReportId])

  // Check for existing unsubmitted local draft backup on mount
  useEffect(() => {
    if (isEditModeActive) return
    try {
      const cached = localStorage.getItem(backupStorageKey)
      if (cached) {
        const parsed = JSON.parse(cached)
        if (parsed && parsed.formData && Object.keys(parsed.formData).length > 0) {
          // Check TTL expiration
          if (parsed.savedAt) {
            const ageMs = Date.now() - new Date(parsed.savedAt).getTime()
            if (ageMs > DRAFT_BACKUP_TTL_MS) {
              localStorage.removeItem(backupStorageKey)
              return
            }
          }

          const hasPassedData = passedFormData && Object.keys(passedFormData).length > 0
          if (!hasPassedData) {
            setPendingLocalDraft({
              formData: parsed.formData,
              consumptionPreviewData: parsed.consumptionPreviewData ?? null,
              savedAt: parsed.savedAt || new Date().toISOString(),
            })
          }
        }
      }
    } catch (e) {
      console.warn('Could not read local draft backup:', e)
    }
  }, [backupStorageKey, isEditModeActive, passedFormData])

  const handleRestoreLocalDraft = () => {
    if (!pendingLocalDraft) return
    setFormData(pendingLocalDraft.formData)
    if (pendingLocalDraft.consumptionPreviewData) {
      setConsumptionPreviewData(pendingLocalDraft.consumptionPreviewData)
    }
    isFormDirtyRef.current = true
    setPendingLocalDraft(null)
    toast.success('Local draft restored successfully.')
  }

  const handleDiscardLocalDraft = () => {
    try {
      localStorage.removeItem(backupStorageKey)
    } catch (e) {
      console.warn('Could not remove local draft backup:', e)
    }
    setPendingLocalDraft(null)
    toast.info('Local draft discarded.')
  }

  // ── Single Active Draft Restriction ──
  // Restrict users to maintaining only one active draft report per voyage.
  // If an active draft already exists for the voyage, block creation and throw user outside immediately.
  useEffect(() => {
    if (isEditModeActive || draftReportData) {
      setIsCheckingActiveDraft(false)
      return
    }
    const targetVesselId =
      voyage?.vessel?.id ??
      selectedVoyage?.vessel?.id ??
      currentUser?.vessel?.id ??
      0
    const targetVoyageId = Number(voyage?.id ?? selectedVoyage?.id ?? 0)

    if (!targetVesselId && !targetVoyageId) {
      setIsCheckingActiveDraft(false)
      return
    }

    let cancelled = false
    setIsCheckingActiveDraft(true)
    getCreatedReportById(targetVesselId || undefined)
      .then((reportsList) => {
        if (cancelled || !Array.isArray(reportsList)) {
          if (!cancelled) setIsCheckingActiveDraft(false)
          return
        }
        const activeDraft = reportsList.find((r: any) => {
          const rStatus = String(r?.status ?? '').trim().toUpperCase()
          if (rStatus !== 'DRAFT') return false
          if (targetVoyageId > 0) {
            const rVoyageId = Number(
              r?.voyage?.id ?? r?.voyageDto?.id ?? r?.voyageId ?? r?.voyage ?? 0
            )
            return rVoyageId === targetVoyageId
          }
          return true
        })

        if (activeDraft) {
          toast.error(
            `An active draft report already exists for this voyage (${activeDraft.reportType || 'Draft Report'}). You cannot create a new report until the existing draft is submitted or deleted.`,
            {position: 'top-center'}
          )
          navigate('/operations/overview', {replace: true})
          return
        }
        setIsCheckingActiveDraft(false)
      })
      .catch((err) => {
        console.warn('Failed to check active draft restriction:', err)
        if (!cancelled) setIsCheckingActiveDraft(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    isEditModeActive,
    draftReportData,
    voyage?.id,
    selectedVoyage?.id,
    voyage?.vessel?.id,
    selectedVoyage?.vessel?.id,
    currentUser?.vessel?.id,
    navigate,
  ])

  const reportActionState = getPositionReportActionState({
    status: isEditModeActive ? draftReportData?.status : ReportStatus.DRAFT,
    canWriteReport,
    isEditMode: isEditModeActive,
    isCrew: scopeUx.isCrew,
    currentUser,
  })

  const isBunkerReport = useMemo(
    () => /\bbunker\b/i.test(String(selectedReport?.name ?? '')),
    [selectedReport?.name]
  )

  const isFuelChangeoverReport = useMemo(
    () =>
      isFuelChangeoverReportName(
        selectedReport?.name ?? draftReportData?.reportType ?? ''
      ),
    [selectedReport?.name, draftReportData?.reportType]
  )

  const resolvedLegId = useMemo(() => {
    const raw =
      selectedLegId ??
      selectedLeg?.id ??
      draftReportData?.legId ??
      (draftReportData as any)?.leg?.id ??
      (draftReportData as any)?.legDto?.id ??
      voyage?.legs?.find((l: any) => l.active)?.id ??
      voyage?.legs?.[0]?.id ??
      selectedVoyage?.legs?.find((l: any) => l.active)?.id ??
      selectedVoyage?.legs?.[0]?.id
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : undefined
  }, [
    selectedLegId,
    selectedLeg,
    draftReportData?.legId,
    (draftReportData as any)?.leg?.id,
    (draftReportData as any)?.legDto?.id,
    voyage?.legs,
    selectedVoyage?.legs,
  ])

  const isMeaningfulConsumptionDto = (dto?: ConsumptionRequestDTO | null) =>
    Boolean(
      dto?.rows?.some(
        (row) =>
          String(row?.fieldType ?? '').trim() !== '' ||
          Number(row?.initialRob ?? 0) !== 0 ||
          Number(row?.lastRob ?? 0) !== 0 ||
          Array.isArray(row?.machineries) &&
            row.machineries.some(
              (machinery) =>
                String(machinery?.machinery ?? '').trim() !== '' ||
                Number(machinery?.consumed ?? 0) !== 0 ||
                Number(machinery?.rob ?? 0) !== 0
            )
      ) ||
      (!isFuelChangeoverReport &&
        (dto as any)?.lubeOils?.some(
          (lube: any) =>
            String(lube?.grade ?? '').trim() !== '' ||
            String(lube?.openingStock ?? '').trim() !== '' ||
            String(lube?.received ?? '').trim() !== '' ||
            String(lube?.consumedByVessel ?? '').trim() !== '' ||
            String(lube?.closingStock ?? '').trim() !== ''
        )) ||
      (isFuelChangeoverReport &&
        (dto as any)?.tanks?.some(
          (t: any) =>
            String(t?.tankName ?? '').trim() !== '' ||
            String(t?.grade ?? '').trim() !== '' ||
            String(t?.initialRob ?? '').trim() !== '' ||
            String(t?.rob ?? '').trim() !== ''
        ))
    )
  const reportHeaderTitle = initialIsEditMode || (draftReportData?.id && Boolean(passedFormData))
    ? `Edit ${selectedReport?.name ?? draftReportData?.reportType ?? 'Report'}`
    : 'Create Report'
  const topActionLabel = reportActionState.primaryDraftLabel

  const [formData, setFormData] = useState<{[tabId: number]: {[fieldId: string]: any}}>(() =>
    passedFormData ? (passedFormData as any) : {}
  )
  const [pendingBunkerAttachments, setPendingBunkerAttachments] = useState<
    PendingReportAttachment[]
  >(passedPendingBunkerAttachments ?? [])
  const [consumptionPreviewData, setConsumptionPreviewData] = useState<ConsumptionRequestDTO | null>(
    passedConsumptionPreviewData ?? null
  )
  const [consumptionRefreshFlag, setConsumptionRefreshFlag] = useState(0)

  const shimmerKeyframes = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `

  const shimmerBaseStyle: CSSProperties = {
    background: 'linear-gradient(90deg, #f5f7fb 0%, #eef1f6 50%, #f5f7fb 100%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.3s ease-in-out infinite',
    borderRadius: '8px',
  }

  const extractReportDateTime = useCallback(
    (sourceFormData: Record<number, Record<string, any>>): string | null => {
      for (const tabData of Object.values(sourceFormData || {})) {
        for (const entry of Object.values(tabData || {})) {
          if (!isReportDateTimeLabel(entry?.label ?? entry?.fieldLabel)) continue
          const raw = entry?.value ?? entry?.valueText
          if (raw == null || String(raw).trim() === '') continue
          return String(raw).trim()
        }
      }
      return null
    },
    []
  )

  useEffect(() => {
    if (selectedTabId && tabs.length > 0) {
      const index = tabs.findIndex((tab) => tab.id === selectedTabId)
      if (index !== -1) {
        setCurrentStep(index)
        fetchFieldsforStep(selectedTabId)
      }
    }
  }, [tabs])

  useEffect(() => {
    if (tabs.length > 0 && currentStep >= 0) {
      const tab = tabs[currentStep]
      fetchFieldsforStep(tab.id)
    }
  }, [currentStep, tabs])

  useEffect(() => {
    const fromForm = extractReportDateTime(formData as any)
    if (fromForm) {
      setReportDate(fromForm)
      return
    }

    const fallback =
      (draftReportData as any)?.reportDateTime ??
      draftReportData?.submittedDateTime ??
      draftReportData?.createdDateTime

    if (fallback) {
      setReportDate(String(fallback))
    }
  }, [draftReportData, extractReportDateTime, formData])

  useEffect(() => {
    const reportId = Number(draftReportData?.id)
    if (Number.isFinite(reportId) && reportId > 0) {
      setActiveReportId(reportId)
    }
  }, [draftReportData?.id])

  const [previousReportsList, setPreviousReportsList] = useState<CreatedReports[]>([])
  const globalFieldLabelMapRef = useRef<Map<number, string>>(new Map())

  useEffect(() => {
    const targetVesselId =
      voyage?.vessel?.id ??
      selectedVoyage?.vessel?.id ??
      draftReportData?.vesselDto?.id ??
      (draftReportData as any)?.vesselId ??
      currentUser?.vessel?.id ??
      selectedReport?.template?.vessel?.id ??
      (selectedReport as any)?.vessel?.id ??
      0
    if (!targetVesselId) return

    let isMounted = true
    getCreatedReportById(targetVesselId)
      .then(async (res) => {
        if (!isMounted || !Array.isArray(res)) return

        // Set raw reports immediately so calculations have data right away
        setPreviousReportsList(res)

        // Extract distinct assignment IDs from reports and current assignment
        const currentAssignId = Number(assignmentId)
        const assignmentIds = Array.from(
          new Set(
            [
              ...res.map((r: any) => Number(r?.assignment?.id)),
              currentAssignId,
            ].filter((id) => Number.isFinite(id) && id > 0)
          )
        )

        // Fetch any missing assignments in parallel
        await Promise.all(
          assignmentIds.map(async (id) => {
            if (assignmentDetailsCache.has(id)) return
            try {
              const assign = await getAssignmentById(id)
              if (assign) assignmentDetailsCache.set(id, assign)
            } catch (e) {
              console.warn(`Failed to fetch assignment ${id} for calculations:`, e)
            }
          })
        )

        if (!isMounted) return

        // Build comprehensive field label map across all cached assignments
        const fieldMap = new Map<number, string>()
        assignmentDetailsCache.forEach((assign) => {
          const tmpl = assign?.customTemplate || assign?.template
          const menus = Array.isArray(tmpl?.menus) ? tmpl.menus : []
          menus.forEach((m: any) => {
            ;(m?.submenus || []).forEach((sm: any) => {
              ;(sm?.fields || []).forEach((f: any) => {
                if (f?.id != null && f?.label) {
                  fieldMap.set(Number(f.id), f.label)
                }
              })
            })
          })
        })

        // Also add fields from current tabs
        tabs.forEach((tab) => {
          ;(tab.fields || []).forEach((f) => {
            if (f?.id != null && f?.label) {
              fieldMap.set(Number(f.id), f.label)
            }
          })
        })

        globalFieldLabelMapRef.current = fieldMap

        // Enrich previous reports with labels in values and template menus
        const enriched = res.map((r: any) => {
          const assign = assignmentDetailsCache.get(Number(r?.assignment?.id))
          const template =
            assign?.customTemplate ||
            assign?.template ||
            r?.assignment?.customTemplate ||
            r?.assignment?.template

          const enrichedValues = Array.isArray(r.values)
            ? r.values.map((v: any) => {
                const fid =
                  typeof v?.field === 'object' && v?.field !== null
                    ? Number(v.field.id)
                    : Number(v?.field)
                const lbl =
                  fieldMap.get(fid) ||
                  v?.label ||
                  (typeof v?.field === 'object' ? v.field.label : '')
                return {
                  ...v,
                  label: lbl,
                  field:
                    typeof v?.field === 'object' && v?.field !== null
                      ? {...v.field, label: lbl}
                      : {id: fid, label: lbl},
                }
              })
            : r.values

          return {
            ...r,
            assignment: {
              ...(r.assignment || {}),
              template,
            },
            values: enrichedValues,
          }
        })

        setPreviousReportsList(enriched)
      })
      .catch((err) => console.warn('Error fetching previous reports for calculations:', err))

    return () => {
      isMounted = false
    }
  }, [
    voyage?.vessel?.id,
    selectedVoyage?.vessel?.id,
    draftReportData?.vesselDto?.id,
    (draftReportData as any)?.vesselId,
    currentUser?.vessel?.id,
    selectedReport?.template?.vessel?.id,
    assignmentId,
  ])

  useEffect(() => {
    if (!tabs.length) return
    const currentVoyageId = Number(
      voyage?.id ??
      selectedVoyage?.id ??
      draftReportData?.voyageDto?.id ??
      (draftReportData as any)?.voyage?.id ??
      (draftReportData as any)?.voyage ??
      (draftReportData as any)?.voyageId ??
      0
    )
    const currentLegId = Number(
      selectedLegId ??
      selectedLeg?.id ??
      draftReportData?.legDto?.id ??
      (draftReportData as any)?.leg?.id ??
      (draftReportData as any)?.legId ??
      0
    )
    const currentReportId = Number(activeReportId ?? draftReportData?.id ?? 0)

    setFormData((previous) => {
      const ciiUpdated = applyCiiSummaryCalculations(tabs, previous)
      return applyPositionReportCalculations(tabs, ciiUpdated, {
        previousReports: previousReportsList,
        currentVoyageId,
        currentLegId,
        currentReportId,
        currentStepFields: fields,
        currentTabId: step?.id,
        fieldLabelMap: globalFieldLabelMapRef.current,
      })
    })
  }, [
    tabs,
    fields,
    step?.id,
    previousReportsList,
    voyage?.id,
    selectedVoyage?.id,
    selectedLegId,
    selectedLeg?.id,
    activeReportId,
    draftReportData,
  ])

  const consumptionTab = useMemo(() => tabs.find((t) => t.name === 'Consumption & ROB'), [tabs])
  const consumptionTabId = consumptionTab?.id

  // Previous report's Miscellaneous Consumables values (create mode only),
  // keyed by normalized (trim+lowercase) field label.
  const [previousMiscConsumption, setPreviousMiscConsumption] = useState<Record<string, string>>({})

  const handleFieldChange = (
    tabId: number,
    label: string,
    id: string,
    value: any,
    isUserInitiated: boolean = true
  ) => {
    if (isUserInitiated) {
      isFormDirtyRef.current = true
    }

    const currentVoyageId = Number(
      voyage?.id ??
      selectedVoyage?.id ??
      draftReportData?.voyageDto?.id ??
      (draftReportData as any)?.voyage?.id ??
      (draftReportData as any)?.voyage ??
      (draftReportData as any)?.voyageId ??
      0
    )
    const currentLegId = Number(
      selectedLegId ??
      selectedLeg?.id ??
      draftReportData?.legDto?.id ??
      (draftReportData as any)?.leg?.id ??
      (draftReportData as any)?.legId ??
      0
    )
    const currentReportId = Number(activeReportId ?? draftReportData?.id ?? 0)

    setFormData((prev) => {
      const updated = {
        ...prev,
        [tabId]: {
          ...(prev as any)[tabId],
          [id]: {label, value},
        },
      }

      const ciiUpdated = applyCiiSummaryCalculations(tabs, updated)
      const calculatedData = tabs.length
        ? applyPositionReportCalculations(tabs, ciiUpdated, {
            previousReports: previousReportsList,
            currentVoyageId,
            currentLegId,
            currentReportId,
            currentStepFields: fields,
            currentTabId: tabId,
            fieldLabelMap: globalFieldLabelMapRef.current,
          })
        : updated

      // 1. Instant local storage backup (survives crashes / network drops)
      try {
        localStorage.setItem(
          backupStorageKey,
          JSON.stringify({
            formData: calculatedData,
            consumptionPreviewData,
            activeReportId: activeReportIdRef.current,
            savedAt: new Date().toISOString(),
          })
        )
      } catch (e) {
        console.warn('Local backup error:', e)
      }

      // 2. Trigger debounced background auto-save to server ONLY when user initiated / form is dirty
      if (isFormDirtyRef.current) {
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current)
        }
        setAutoSaveStatus('SAVING')
        autoSaveTimerRef.current = setTimeout(() => {
          performAutoSaveDraftRef.current?.(calculatedData)
        }, 1500)
      }

      return calculatedData
    })
  }

  const resolveTabIdByFieldId = (fieldId: string, fallbackTabId: number): number => {
    const numericFieldId = Number(fieldId)
    if (!Number.isFinite(numericFieldId)) return fallbackTabId

    const owningTab = tabs.find((tab) =>
      (tab.fields || []).some((field) => Number(field.id) === numericFieldId)
    )

    return owningTab?.id ?? fallbackTabId
  }

  // ---------- robust parsing helpers ----------
  const smartParse = <T,>(raw: any, fallback: T): T => {
    if (raw === undefined || raw === null || raw === '') return fallback
    if (typeof raw !== 'string') return raw as T
    try {
      return JSON.parse(raw) as T
    } catch {
      return raw as unknown as T
    }
  }

  const asStringArray = (v: any): string[] => {
    if (v === undefined || v === null || v === '') return []
    if (Array.isArray(v)) return v.map((x) => String(x))
    return [String(v)]
  }

  const asString2DArray = (v: any): string[][] => {
    if (!Array.isArray(v)) return []
    return v.map((row: any) => (Array.isArray(row) ? row.map(String) : [String(row)]))
  }

  // ---------- dynamic field id resolver ----------
  const getConsumptionFieldIds = () => {
    if (!consumptionTabId) return null
    const submenu = selectedReport?.submenus?.find((sm) => sm.id === consumptionTabId)
    if (!submenu?.fields?.length) return null

    const norm = (s: any) =>
      String(s ?? '')
        .trim()
        .toLowerCase()

    const isInitialRobLabel = (label: any) => {
      const l = norm(label)
      return (
        l.includes('initial rob') ||
        l.includes('inital rob') ||
        (l.includes('opening') && l.includes('rob'))
      )
    }

    const byLabel = (label: string) =>
      submenu.fields.filter((f) => norm(f.label).includes(norm(label)))

    const fuel = byLabel('Fuel Type')[0]
    const initial = submenu.fields.find((f) => isInitialRobLabel(f.label))
    const robs = submenu.fields.filter((f) => {
      const l = norm(f.label)
      return l.includes('rob') && !isInitialRobLabel(l)
    })
    const outerRob = robs[0]
    const innerRob = robs[1]
    const machinery = byLabel('Machinery')[0]
    const operationType = byLabel('Operation Type')[0]
    const consumed = byLabel('Consumed')[0]

    if (!fuel?.id || !outerRob?.id || !innerRob?.id || !machinery?.id || !consumed?.id) return null

    return {
      fuelId: fuel.id,
      initialId: initial?.id,
      outerRobId: outerRob.id,
      innerRobId: innerRob.id,
      machineryId: machinery.id,
      operationTypeId: operationType?.id,
      consumedId: consumed.id,
    }
  }

  // ---------- build dto from formData ----------
  const buildConsumptionDtoFromFormData = (): ConsumptionRequestDTO | null => {
    if (!voyage?.id) return null
    if (!consumptionTabId) return null

    const ids = getConsumptionFieldIds()
    if (!ids) return null

    const tabData = (formData as any)[consumptionTabId]
    if (!tabData) return null

    const fuelTypes = asStringArray(smartParse<any>(tabData[ids.fuelId]?.value, []))
    const initialRobs = ids.initialId
      ? asStringArray(smartParse<any>(tabData[ids.initialId]?.value, []))
      : []
    const outerRobs = asStringArray(smartParse<any>(tabData[ids.outerRobId]?.value, []))
    const machineries = asString2DArray(smartParse<any>(tabData[ids.machineryId]?.value, []))
    const operationTypes = ids.operationTypeId
      ? asString2DArray(smartParse<any>(tabData[ids.operationTypeId]?.value, []))
      : []
    const consumed = asString2DArray(smartParse<any>(tabData[ids.consumedId]?.value, []))
    const innerRobs = asString2DArray(smartParse<any>(tabData[ids.innerRobId]?.value, []))

    const outerCount = Math.max(
      fuelTypes.length,
      initialRobs.length,
      outerRobs.length,
      machineries.length,
      0
    )
    if (outerCount === 0) return null

    const userId = Number(currentUser?.id ?? 0)
    const companyId = Number(
      currentUser?.company?.id ??
        (currentUser as any)?.companyId ??
        (currentUser as any)?.companyAdminId ??
        (currentUser as any)?.companyGroupAdminId ??
        0
    )

    const rows = Array.from({length: outerCount})
      .map((_, i) => {
        const ft = (fuelTypes[i] ?? '').trim()
        if (!ft) return null

        const rawInit = initialRobs[i]
        const rawLast = outerRobs[i]

        // Fallback: if initial ROB not provided (auto-populated), use outer ROB value
        const init =
          rawInit === undefined || rawInit === null || rawInit === ''
            ? Number(rawLast ?? 0)
            : Number(rawInit)
        const last = Number(rawLast ?? 0)

        const mArr = machineries[i] ?? []
        const oArr = operationTypes[i] ?? []
        const cArr = consumed[i] ?? []
        const rArr = innerRobs[i] ?? []

        const machs = mArr
          .map((m, j) => {
            const machinery = String(m ?? '').trim()
            if (!machinery) return null

            const cons = Number(cArr[j] ?? 0)
            const rob = Number(rArr[j] ?? 0)

            return {
              machinery,
              operationType: String(oArr[j] ?? '').trim(),
              consumed: isNaN(cons) ? 0 : cons,
              rob: isNaN(rob) ? 0 : rob,
            }
          })
          .filter(Boolean) as {
          machinery?: string
          operationType?: string
          consumed?: number
          rob?: number
        }[]

        const prevRow = consumptionPreviewData?.rows?.[i]
        return {
          fieldType: ft,
          tankName: prevRow?.tankName || '',
          q88Grade: prevRow?.q88Grade || '',
          sulphurPercentage: prevRow?.sulphurPercentage || '',
          allocationStatus: prevRow?.allocationStatus || '',
          initialRob: isNaN(init) ? 0 : init,
          lastRob: isNaN(last) ? 0 : last,
          machineries: machs,
        }
      })
      .filter(Boolean) as ConsumptionRequestDTO['rows']

    if (!rows || rows.length === 0) return null

    const refDto = consumptionRobRef.current?.getDto?.()
    return {
      reportId: undefined, // will be set in persistConsumption(reportId)
      userId,
      companyId,
      voyageId: Number(voyage.id),
      remarks: consumptionPreviewData?.remarks ?? '',
      rows,
      lubeOils: refDto?.lubeOils ?? consumptionPreviewData?.lubeOils ?? [],
      tanks: refDto?.tanks ?? consumptionPreviewData?.tanks ?? undefined,
    }
  }

  // ✅ submit consumption snapshot for a specific reportId
  const persistConsumption = async (reportId: number) => {
    // Prefer live DTO from the consumption component; fallback to formData-derived DTO
    const refDto = consumptionRobRef.current?.getDto?.()
    const dto =
      (isMeaningfulConsumptionDto(refDto) && refDto) ||
      (isMeaningfulConsumptionDto(consumptionPreviewData) && consumptionPreviewData) ||
      buildConsumptionDtoFromFormData()
    if (!dto) return

    // Normalize report id defensively (API sometimes returns nested/array responses)
    const toNumber = (v: any) => {
      const n = Number(v)
      return Number.isFinite(n) ? n : undefined
    }

    const normalizedReportId =
      toNumber(reportId) ?? toNumber((dto as any)?.reportId) ?? toNumber(draftReportData?.id)

    if (normalizedReportId === undefined) {
      console.warn('Consumption submit missing reportId; check submitReport response.', {
        reportIdParam: reportId,
        dtoReportId: (dto as any)?.reportId,
        draftId: draftReportData?.id,
      })
    }

    const resolvedLegId =
      draftReportData?.legId ??
      (draftReportData as any)?.leg?.id ??
      (draftReportData as any)?.legDto?.id ??
      selectedLegId ??
      selectedLeg?.id ??
      voyage?.legs?.find((l: any) => l.active)?.id ??
      voyage?.legs?.[0]?.id ??
      selectedVoyage?.legs?.find((l: any) => l.active)?.id ??
      selectedVoyage?.legs?.[0]?.id

    const normalized = {
      ...dto,
      reportId: normalizedReportId,
      voyageId: Number(voyage?.id ?? dto.voyageId ?? 0),
      legId: resolvedLegId && !isNaN(Number(resolvedLegId)) ? Number(resolvedLegId) : undefined,
      userId: Number(dto.userId ?? currentUser?.id ?? 0),
      companyId: Number(
        dto.companyId ??
          currentUser?.company?.id ??
          (currentUser as any)?.companyId ??
          (currentUser as any)?.companyAdminId ??
          (currentUser as any)?.companyGroupAdminId ??
          0
      ),
    }

    await submitConsumptionROB(token, normalized)
  }

  const persistBunkerAttachments = async (reportId: number) => {
    if (!isBunkerReport || pendingBunkerAttachments.length === 0) return

    const itemsToUpload = pendingBunkerAttachments.filter((item) => Boolean(item.file))
    if (itemsToUpload.length === 0) return

    for (const item of itemsToUpload) {
      await uploadReportAttachment(reportId, item.file as File, item.documentName, item.remarks)
    }
  }

  const buildBunkerAttachmentValuesWithUploadedIds = async (
    reportId: number,
    valuesToPatch: {field: number; valueText: string}[]
  ) => {
    if (!isBunkerReport || !selectedReport?.submenus?.length) return valuesToPatch

    const bunkerAttachmentLabels = new Set([
      normalize(BUNKER_FIELD_LABEL_LOOKUP.attachmentRows),
      normalize('Attachment Rows'),
    ])

    const attachmentField = selectedReport.submenus
      .flatMap((submenu) => submenu.fields || [])
      .find((field) => bunkerAttachmentLabels.has(normalize(field.label)))

    if (!attachmentField?.id) return valuesToPatch

    const fieldId = Number(attachmentField.id)
    const targetIndex = valuesToPatch.findIndex((item) => Number(item.field) === fieldId)
    if (targetIndex < 0) return valuesToPatch

    const rawValue = valuesToPatch[targetIndex]?.valueText
    if (!rawValue) return valuesToPatch

    let parsed: any
    try {
      parsed = JSON.parse(rawValue)
    } catch {
      return valuesToPatch
    }

    const uploadedFromPayload = Array.isArray(parsed?.uploaded) ? parsed.uploaded : []
    const pendingFromPayload = Array.isArray(parsed?.pending) ? parsed.pending : []

    const normalizeBlock = (value: any) => {
      const parsedValue = Number(value)
      return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 1
    }

    const attachmentKey = (documentName: any, fileName: any) =>
      `${normalize(documentName)}|${normalize(fileName)}`

    const blockById = new Map<number, number>()
    const blockByKey = new Map<string, number>()

    for (const item of [...uploadedFromPayload, ...pendingFromPayload]) {
      const block = normalizeBlock(item?.reportBlock)
      const id = Number(item?.id)
      if (Number.isFinite(id)) {
        blockById.set(id, block)
      }

      const key = attachmentKey(item?.documentName, item?.fileName)
      if (key !== '|') {
        blockByKey.set(key, block)
      }
    }

    const uploadedFromServer = await listReportAttachments(reportId)
    if (!Array.isArray(uploadedFromServer) || uploadedFromServer.length === 0) return valuesToPatch

    const uploaded = uploadedFromServer.map((item: any) => {
      const id = Number(item?.id)
      const key = attachmentKey(item?.documentName || item?.fileName, item?.fileName)
      const block = blockById.get(id) || blockByKey.get(key) || 1

      return {
        id,
        reportId,
        reportBlock: block,
        documentName: String(item?.documentName || item?.fileName || '--'),
        remarks: String(item?.remarks || ''),
        fileName: String(item?.fileName || '--'),
      }
    })

    const patchedPayload = JSON.stringify({uploaded, pending: []})
    const nextValues = [...valuesToPatch]
    nextValues[targetIndex] = {
      ...nextValues[targetIndex],
      valueText: patchedPayload,
    }

    return nextValues
  }

  const normalize = (s: any) => String(s ?? '').trim().toLowerCase()

  const validateBunkerAttachmentRules = () => {
    if (!isBunkerReport) return true

    const hasMissingType = pendingBunkerAttachments.some(
      (item) => item?.file && !String(item?.documentName || '').trim()
    )

    if (hasMissingType) {
      toast.error('Please select attachment type for each bunker row attachment.')
      return false
    }

    return true
  }

  const hasBdmAttachmentSelectedForSubmit = (values: {field: number; valueText: string}[]) => {
    if (!isBunkerReport) return true

    const bdmNorm = normalize(BUNKER_REQUIRED_ATTACHMENT_DOCUMENT_NAME)
    const hasBdmPendingFile = pendingBunkerAttachments.some(
      (item) =>
        normalize(item?.documentName) === bdmNorm &&
        (Boolean(item?.file) || String(item?.fileName || '').trim().length > 0)
    )
    if (hasBdmPendingFile) return true

    const attachmentLabels = new Set([
      normalize(BUNKER_FIELD_LABEL_LOOKUP.attachmentRows),
      normalize('Attachment Rows'),
    ])
    const attachmentField = selectedReport?.submenus
      ?.flatMap((sm) => sm.fields || [])
      .find((f) => attachmentLabels.has(normalize(f.label)))

    if (!attachmentField?.id) return false

    const target = values.find((item) => Number(item.field) === Number(attachmentField.id))
    if (!target?.valueText) return false

    try {
      const parsed = JSON.parse(target.valueText)
      const uploaded = Array.isArray(parsed?.uploaded) ? parsed.uploaded : []
      const pending = Array.isArray(parsed?.pending) ? parsed.pending : []
      return [...uploaded, ...pending].some(
        (item: any) =>
          normalize(item?.documentName) === bdmNorm && String(item?.fileName || '').trim().length > 0
      )
    } catch {
      return false
    }
  }

  // ---------- validations ----------
  const runValidationForAllSteps = (
    customFormData?: typeof formData
  ): {
    isValid: boolean
    totalWarnings: number
    stepWarnings: Record<number, number>
    warningFields: {stepId: number; fieldId: number; message: string}[]
    stepErrors: Record<number, number>
    errorFields: {stepId: number; stepName: string; fieldId: number; fieldLabel: string; message: string}[]
  } => {
    let isValid = true
    let totalWarnings = 0
    const stepWarnings: Record<number, number> = {}
    const stepErrors: Record<number, number> = {}
    const warningFieldList: {stepId: number; fieldId: number; message: string}[] = []
    const errorFieldList: {stepId: number; stepName: string; fieldId: number; fieldLabel: string; message: string}[] = []
    const dataToValidate = customFormData || formData
    const hiddenLegacyBunkerLabels = new Set([
      'year',
      'vessel',
      'date (utc)',
      'time (utc)',
      "hfo (mt's)",
      "mgo (mt's)",
      'bunker attachments',
      'attachment rows',
    ])

    for (const tab of tabs) {
      if (tab.name === 'Consumption & ROB') {
        stepWarnings[tab.id] = 0
        stepErrors[tab.id] = 0
        continue
      }

      const submenu = selectedReport?.submenus?.find((sm) => sm.id === tab.id)
      const tabFields = sortFieldsByOrder(submenu?.fields?.filter((f) => f.isActive) || [])
      const tabValues = (dataToValidate as any)[tab.id] || {}

      for (const field of tabFields) {
        if (isBunkerReport && hiddenLegacyBunkerLabels.has(normalize(field.label))) {
          continue
        }

        const value = tabValues[field.id]?.value
        const error = validateField(field, value)
        if (error) {
          const isSoft = field.warningOnly === WarningLevel.SOFT
          const isNumber = field.fieldType.toLowerCase() === 'number'
          if (isSoft && isNumber) {
            stepWarnings[tab.id] = (stepWarnings[tab.id] || 0) + 1
            warningFieldList.push({stepId: tab.id, fieldId: field.id, message: error})
            totalWarnings++
          } else {
            isValid = false
            stepErrors[tab.id] = (stepErrors[tab.id] || 0) + 1
            errorFieldList.push({
              stepId: tab.id,
              stepName: tab.name,
              fieldId: field.id,
              fieldLabel: field.label,
              message: error,
            })
          }
        }
      }
    }

    return {
      isValid,
      totalWarnings,
      stepWarnings,
      warningFields: warningFieldList,
      stepErrors,
      errorFields: errorFieldList,
    }
  }

  const extractReportRemark = (): string => {
    const refDto = consumptionRobRef.current?.getDto?.()
    if (refDto?.remarks && String(refDto.remarks).trim() !== '') {
      return String(refDto.remarks).trim()
    }
    if (consumptionPreviewData?.remarks && String(consumptionPreviewData.remarks).trim() !== '') {
      return String(consumptionPreviewData.remarks).trim()
    }
    if (formData && typeof formData === 'object') {
      for (const tabData of Object.values(formData)) {
        if (!tabData || typeof tabData !== 'object') continue
        for (const [key, entry] of Object.entries(tabData as any)) {
          const lbl = String((entry as any)?.label || key).toLowerCase()
          if (lbl.includes('remark') || lbl.includes('comment')) {
            const val = (entry as any)?.value ?? entry
            if (typeof val === 'string' && val.trim() !== '') {
              return val.trim()
            }
          }
        }
      }
    }
    if (draftReportData?.remark && String(draftReportData.remark).trim() !== '') {
      return String(draftReportData.remark).trim()
    }
    return ''
  }

  const mapCreatedValuesToFormData = (values: CreatedReports['values'], menus: any[]) => {
    const result: any = {}
    if (!values || !Array.isArray(values) || !menus || !Array.isArray(menus)) return result

    const norm = (s: any) =>
      String(s ?? '')
        .trim()
        .toLowerCase()

    const unwrapScalarValue = (raw: string): string => {
      if (typeof raw === 'string' && raw.startsWith('[') && raw.endsWith(']')) {
        try {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) {
            const last = parsed[parsed.length - 1]
            return last !== undefined && last !== null ? String(last) : ''
          }
        } catch {}
      }
      return raw
    }

    for (const entry of values) {
      const rawField = (entry as any)?.field
      const fieldId =
        typeof rawField === 'object' && rawField !== null
          ? Number(rawField.id)
          : Number(rawField)
      const entryLabel = norm(
        (entry as any)?.label ?? (typeof rawField === 'object' ? rawField?.label : '')
      )

      if (Number.isNaN(fieldId) && !entryLabel) continue

      const rawFieldValue =
        entry.valueText !== undefined && entry.valueText !== null
          ? String(entry.valueText)
          : (entry as any).value !== undefined && (entry as any).value !== null
          ? String((entry as any).value)
          : ''

      for (const menu of menus) {
        if (!menu?.submenus) continue
        for (const submenu of menu.submenus) {
          let matchingField = submenu.fields?.find((field: any) => Number(field?.id) === fieldId)
          if (!matchingField && entryLabel) {
            matchingField = submenu.fields?.find((field: any) => norm(field?.label) === entryLabel)
          }

          if (matchingField) {
            const submenuId = submenu.id
            const targetFieldId = Number(matchingField.id)
            const isCheckbox = matchingField.fieldType === 'checkbox'
            const fieldValue = isCheckbox ? rawFieldValue : unwrapScalarValue(rawFieldValue)

            if (!result[submenuId]) result[submenuId] = {}
            if (!result[submenuId][targetFieldId]) {
              result[submenuId][targetFieldId] = {
                label: matchingField.label,
                value: isCheckbox ? [fieldValue] : fieldValue,
              }
            } else if (isCheckbox) {
              if (Array.isArray(result[submenuId][targetFieldId].value)) {
                result[submenuId][targetFieldId].value.push(fieldValue)
              } else {
                result[submenuId][targetFieldId].value = [result[submenuId][targetFieldId].value, fieldValue]
              }
            }
          }
        }
      }
    }
    return result
  }

  const buildValuesFromTabData = (tabData: any): {field: number; valueText: string}[] => {
    const out: {field: number; valueText: string}[] = []
    if (!tabData) return out

    const cleanScalar = (val: any): string => {
      if (val === undefined || val === null) return ''
      const s = String(val)
      if (s.startsWith('[') && s.endsWith(']')) {
        try {
          const parsed = JSON.parse(s)
          if (Array.isArray(parsed) && parsed.length > 0) {
            const last = parsed[parsed.length - 1]
            return last !== undefined && last !== null ? String(last) : ''
          }
        } catch {}
      }
      return s
    }

    Object.entries(tabData).forEach(([fieldId, entry]: any) => {
      const numId = Number(fieldId)
      if (Number.isNaN(numId)) return

      if (entry === null || entry === undefined) {
        out.push({field: numId, valueText: ''})
      } else if (typeof entry === 'object' && !Array.isArray(entry) && entry.value !== undefined) {
        if (Array.isArray(entry.value)) {
          entry.value.forEach((val: any) =>
            out.push({field: numId, valueText: val !== undefined && val !== null ? String(val) : ''})
          )
        } else {
          out.push({field: numId, valueText: cleanScalar(entry.value)})
        }
      } else if (Array.isArray(entry)) {
        entry.forEach((val: any) =>
          out.push({field: numId, valueText: val !== undefined && val !== null ? String(val) : ''})
        )
      } else {
        out.push({field: numId, valueText: cleanScalar(entry)})
      }
    })
    return out
  }

  const buildFinalReportValues = (sourceFormData: any = formData): {field: number; valueText: string}[] => {
    const merged = new Map<number, string>()
    Object.entries(sourceFormData || {}).forEach(([_, tabData]) => {
      buildValuesFromTabData(tabData).forEach((v) => merged.set(v.field, v.valueText))
    })
    return Array.from(merged.entries())
      .filter(([field]) => Number.isFinite(field) && field > 0 && field < 90000)
      .map(([field, valueText]) => ({field, valueText}))
  }

  // Extract report id from API response (handles varied shapes from backend)
  const extractReportId = (created: any): number | null => {
    if (!created) return null
    const visited = new WeakSet<any>()

    const walk = (node: any): number | null => {
      if (node === null || node === undefined) return null
      if (typeof node !== 'object') return null
      if (visited.has(node)) return null
      visited.add(node)

      for (const key of ['reportId', 'id']) {
        const n = Number((node as any)?.[key])
        if (Number.isFinite(n)) return n
      }

      if (Array.isArray(node)) {
        for (const item of node) {
          const found = walk(item)
          if (found !== null) return found
        }
      }

      for (const value of Object.values(node)) {
        const found = walk(value)
        if (found !== null) return found
      }

      return null
    }

    return walk(created)
  }

  const renderFieldLoader = () => (
    <div className='d-flex flex-column gap-3'>
      {Array.from({length: 3}).map((_, idx) => (
        <div key={idx} className='border rounded-3 p-4 bg-white shadow-sm'>
          <div style={{...shimmerBaseStyle, height: '14px', width: '40%', marginBottom: '12px'}} />
          <div className='d-flex flex-column gap-2'>
            <div style={{...shimmerBaseStyle, height: '12px', width: '100%'}} />
            <div style={{...shimmerBaseStyle, height: '12px', width: '85%'}} />
            <div style={{...shimmerBaseStyle, height: '12px', width: '70%'}} />
          </div>
        </div>
      ))}
    </div>
  )



  const hasPendingAutoSaveRef = useRef<boolean>(false)

  // ── Auto-Save Draft in Background (Debounced & Non-blocking) ──
  const performAutoSaveDraft = useCallback(
    async (
      latestFormData?: any,
      latestConsumption?: any
    ) => {
      if (
        reportActionInFlightRef.current ||
        submitActionConsumedRef.current
      ) {
        return
      }

      if (isAutoSavingRef.current) {
        hasPendingAutoSaveRef.current = true
        return
      }

      if (!reportActionState.canSaveDraft) return

      // Auto-save must not execute in create mode if user has not modified the form
      if (!isFormDirtyRef.current && !isEditModeActive) {
        return
      }

      const targetFormData = latestFormData || formData
      const finalValues = buildFinalReportValues(targetFormData)
      if (!finalValues || finalValues.length === 0) return

      const vesselId =
        voyage?.vessel?.id ??
        (voyage as any)?.vesselId ??
        draftReportData?.vesselDto?.id ??
        (draftReportData as any)?.vesselId ??
        (draftReportData as any)?.vessel?.id ??
        currentUser?.vessel?.id
      const vesselIdNumber = vesselId && !isNaN(Number(vesselId)) ? Number(vesselId) : undefined

      const legId =
        draftReportData?.legId ??
        (draftReportData as any)?.leg?.id ??
        (draftReportData as any)?.legDto?.id ??
        selectedLegId ??
        selectedLeg?.id ??
        voyage?.legs?.find((l: any) => l.active)?.id ??
        voyage?.legs?.[0]?.id ??
        selectedVoyage?.legs?.find((l: any) => l.active)?.id ??
        selectedVoyage?.legs?.[0]?.id
      const legIdNumber = legId && !isNaN(Number(legId)) ? Number(legId) : undefined

      // 1. Instant local storage backup (prevents data loss during satellite drops/crashes)
      try {
        localStorage.setItem(
          backupStorageKey,
          JSON.stringify({
            formData: targetFormData,
            consumptionPreviewData: latestConsumption || consumptionPreviewData,
            activeReportId: activeReportIdRef.current,
            savedAt: new Date().toISOString(),
          })
        )
      } catch (e) {
        console.warn('Local backup failed:', e)
      }

      // 2. Server-side Draft Persistence
      setAutoSaveStatus('SAVING')
      setAutoSaveError(null)
      isAutoSavingRef.current = true

      try {
        const currentId = activeReportIdRef.current || draftReportData?.id
        const reportRemark = extractReportRemark()
        const hasReview = Boolean(reportRemark && reportRemark.trim() !== '')

        if (currentId) {
          let valuesForSave = finalValues
          if (isBunkerReport) {
            await persistBunkerAttachments(currentId)
            valuesForSave = await buildBunkerAttachmentValuesWithUploadedIds(
              currentId,
              valuesForSave
            )
          }

          await updatedSubmittedReport(
            currentId,
            selectedReport?.name ?? draftReportData?.reportType ?? 'Unnamed Report',
            ReportStatus.DRAFT,
            false,
            voyage?.id ?? 0,
            Number(assignmentId),
            valuesForSave,
            vesselIdNumber,
            0,
            hasReview,
            reportRemark,
            undefined,
            undefined,
            undefined,
            undefined,
            legIdNumber
          )

          await persistConsumption(currentId)

          setAutoSaveStatus('SAVED')
          setLastAutoSavedTime(new Date())
        } else {
          const created: any = await submitReport(
            selectedReport?.name ?? 'Unnamed Report',
            ReportStatus.DRAFT,
            false,
            voyage?.id ?? 0,
            Number(assignmentId),
            finalValues,
            vesselIdNumber,
            0,
            hasReview,
            reportRemark,
            undefined,
            undefined,
            undefined,
            undefined,
            legIdNumber
          )

          const createdId = extractReportId(created)
          if (createdId) {
            activeReportIdRef.current = createdId
            setActiveReportId(createdId)
            setIsEditMode(true)
            setDraftReportData((prev) => ({
              ...(prev as any),
              id: createdId,
              status: ReportStatus.DRAFT,
              values: finalValues as any,
            }))

            await persistConsumption(createdId)
            await persistBunkerAttachments(createdId)

            setAutoSaveStatus('SAVED')
            setLastAutoSavedTime(new Date())
          }
        }
      } catch (err: any) {
        console.warn('Draft auto-save warning (saved to local backup):', err)
        setAutoSaveStatus('OFFLINE')
        setAutoSaveError(err?.message || 'Offline backup active')
      } finally {
        isAutoSavingRef.current = false
        if (hasPendingAutoSaveRef.current && isFormDirtyRef.current) {
          hasPendingAutoSaveRef.current = false
          if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
          autoSaveTimerRef.current = setTimeout(() => {
            performAutoSaveDraftRef.current?.()
          }, 300)
        }
      }
    },
    [
      formData,
      consumptionPreviewData,
      voyage,
      draftReportData,
      assignmentId,
      selectedReport,
      selectedLegId,
      selectedLeg,
      isBunkerReport,
      currentUser,
      backupStorageKey,
      reportActionState.canSaveDraft,
    ]
  )

  useEffect(() => {
    performAutoSaveDraftRef.current = performAutoSaveDraft
  }, [performAutoSaveDraft])

  // ✅ DRAFT: save report first -> then consumption(reportId)
  const executeSaveDraft = async () => {
    if (reportActionInFlightRef.current) return

    const {totalWarnings, stepWarnings, warningFields: fieldWarnings} = runValidationForAllSteps()
    setWarningsByStep(stepWarnings)
    setHasWarningsByStep(
      Object.fromEntries(tabs.map((tab) => [tab.id, (stepWarnings[tab.id] || 0) > 0]))
    )
    setWarningFields(fieldWarnings)
    setTotalWarnings(totalWarnings)
    setHasWarnings(totalWarnings > 0)

    const hasAnyWarnings = totalWarnings > 0
    if (!validateBunkerAttachmentRules()) return

    if (!reportActionState.canSaveDraft) {
      toast.error('You do not have permission to save this report.')
      return
    }

    const finalValues = buildFinalReportValues()
    const vesselId =
      voyage?.vessel?.id ??
      (voyage as any)?.vesselId ??
      draftReportData?.vesselDto?.id ??
      (draftReportData as any)?.vesselId ??
      (draftReportData as any)?.vessel?.id ??
      currentUser?.vessel?.id
    const vesselIdNumber = vesselId && !isNaN(Number(vesselId)) ? Number(vesselId) : undefined

    const legId =
      draftReportData?.legId ??
      (draftReportData as any)?.leg?.id ??
      (draftReportData as any)?.legDto?.id ??
      selectedLegId ??
      selectedLeg?.id ??
      voyage?.legs?.find((l: any) => l.active)?.id ??
      voyage?.legs?.[0]?.id ??
      selectedVoyage?.legs?.find((l: any) => l.active)?.id ??
      selectedVoyage?.legs?.[0]?.id
    const legIdNumber = legId && !isNaN(Number(legId)) ? Number(legId) : undefined

    reportActionInFlightRef.current = true
    setIsReportActionInProgress(true)

    try {
      const preservedStatus = normalizeSavedStatus(draftReportData?.status)
      const reportRemark = extractReportRemark()
      const hasReview = Boolean(reportRemark && reportRemark.trim() !== '')

      if (isEditModeActive) {
        let valuesForSave = finalValues

        if (isBunkerReport) {
          await persistBunkerAttachments(draftReportData!.id)
          valuesForSave = await buildBunkerAttachmentValuesWithUploadedIds(
            draftReportData!.id,
            valuesForSave
          )
        }

        // update report first
        await updatedSubmittedReport(
          draftReportData!.id,
          selectedReport?.name ?? 'Unnamed Report',
          preservedStatus,
          hasAnyWarnings,
          voyage?.id ?? 0,
          Number(assignmentId),
          valuesForSave,
          vesselIdNumber,
          totalWarnings,
          hasReview,
          reportRemark,
          undefined,
          undefined,
          undefined,
          undefined,
          legIdNumber
        )

        await persistConsumption(draftReportData!.id)

        toast.success('Draft saved successfully.')
      } else {
        // create report first (must return created report with id)
        const created: any = await submitReport(
          selectedReport?.name ?? 'Unnamed Report',
          ReportStatus.DRAFT,
          hasAnyWarnings,
          voyage?.id ?? 0,
          Number(assignmentId),
          finalValues,
          vesselIdNumber,
          totalWarnings,
          hasReview,
          reportRemark,
          undefined,
          undefined,
          undefined,
          undefined,
          legIdNumber
        )

        const createdId = extractReportId(created)
        if (!createdId) {
          toast.error(
            'Report saved but report id not returned from API. Update backend to return created report id.'
          )
          return
        }

        await persistConsumption(createdId)

        await persistBunkerAttachments(createdId)

        if (isBunkerReport) {
          const valuesForSave = await buildBunkerAttachmentValuesWithUploadedIds(createdId, finalValues)

          await updatedSubmittedReport(
            createdId,
            selectedReport?.name ?? 'Unnamed Report',
            ReportStatus.DRAFT,
            hasAnyWarnings,
            voyage?.id ?? 0,
            Number(assignmentId),
            valuesForSave,
            vesselIdNumber,
            totalWarnings,
            hasReview,
            reportRemark,
            undefined,
            undefined,
            undefined,
            undefined,
            legIdNumber
          )
        }

        toast.success('Draft saved successfully.')
      }

      try {
        localStorage.removeItem(backupStorageKey)
      } catch (e) {}

      setTimeout(() => navigate('/operations/overview'), 1500)
    } catch (err) {
      console.error('Failed to save draft:', err)
      toast.error(getApiErrorMessage(err, 'Failed to save draft. Please try again.'))
    } finally {
      reportActionInFlightRef.current = false
      setIsReportActionInProgress(false)
    }
  }

  const handleSaveDraft = () => {
    if (reportActionInFlightRef.current) return

    if (isSuperAdmin) {
      setModalActionType('draft')
      setIsConfirmOverWriteModalOpen(true)
      return
    }

    void executeSaveDraft()
  }

  const handleConfirmOverwriteSave = async () => {
    setIsConfirmOverWriteModalOpen(false)
    await executeSaveDraft()
  }

  // ✅ SUBMIT: save report first -> then consumption(reportId)
  const executeSubmit = async () => {
    if (reportActionInFlightRef.current || submitActionConsumedRef.current) return

    const {
      isValid,
      totalWarnings,
      stepWarnings,
      warningFields: fieldWarnings,
      stepErrors,
      errorFields: fieldErrors,
    } = runValidationForAllSteps()
    setWarningsByStep(stepWarnings)
    setHasWarningsByStep(
      Object.fromEntries(tabs.map((tab) => [tab.id, (stepWarnings[tab.id] || 0) > 0]))
    )
    setWarningFields(fieldWarnings)
    setTotalWarnings(totalWarnings)
    setHasWarnings(totalWarnings > 0)
    setErrorsByStep(stepErrors)
    setHasErrorsByStep(
      Object.fromEntries(tabs.map((tab) => [tab.id, (stepErrors[tab.id] || 0) > 0]))
    )
    setErrorFields(fieldErrors)

    if (!isValid) {
      const firstError = fieldErrors[0]
      if (firstError) {
        const targetTabIndex = tabs.findIndex((t) => t.id === firstError.stepId)
        if (targetTabIndex !== -1 && targetTabIndex !== currentStep) {
          goToStep(targetTabIndex)
          fetchFieldsforStep(firstError.stepId)
        }
        toast.error(`Please fix errors in "${firstError.stepName}": ${firstError.fieldLabel} (${firstError.message})`)
      } else {
        toast.error('Fix errors before submitting.')
      }
      return
    }

    if (totalWarnings > 0 && !window.confirm(`${totalWarnings} warnings found. Submit anyway?`))
      return

    if (!validateBunkerAttachmentRules()) return

    if (!reportActionState.canSaveDraft) {
      toast.error('You do not have permission to save this report.')
      return
    }

    if (!reportActionState.canSubmit) {
      toast.error('You do not have permission to submit this report.')
      return
    }

    const finalValues = buildFinalReportValues()
    if (!hasBdmAttachmentSelectedForSubmit(finalValues)) {
      toast.error('BDM attachment is required before submitting this bunker report.')
      return
    }
    const vesselId =
      voyage?.vessel?.id ??
      (voyage as any)?.vesselId ??
      draftReportData?.vesselDto?.id ??
      (draftReportData as any)?.vesselId ??
      (draftReportData as any)?.vessel?.id ??
      currentUser?.vessel?.id
    const vesselIdNumber = vesselId && !isNaN(Number(vesselId)) ? Number(vesselId) : undefined

    const legId =
      draftReportData?.legId ??
      (draftReportData as any)?.leg?.id ??
      (draftReportData as any)?.legDto?.id ??
      selectedLegId ??
      selectedLeg?.id ??
      voyage?.legs?.find((l: any) => l.active)?.id ??
      voyage?.legs?.[0]?.id ??
      selectedVoyage?.legs?.find((l: any) => l.active)?.id ??
      selectedVoyage?.legs?.[0]?.id
    const legIdNumber = legId && !isNaN(Number(legId)) ? Number(legId) : undefined

    const statusToSend = ReportStatus.SUBMITTED
    const shouldFinalizeSubmit = !isEditModeActive

    submitActionConsumedRef.current = true
    setIsSubmitActionConsumed(true)
    reportActionInFlightRef.current = true
    setIsReportActionInProgress(true)

    try {
      const reportRemark = extractReportRemark()
      const hasReview = Boolean(reportRemark && reportRemark.trim() !== '')

      if (isEditModeActive) {
        let valuesForSubmit = finalValues

        await persistBunkerAttachments(draftReportData!.id)
        valuesForSubmit = await buildBunkerAttachmentValuesWithUploadedIds(
          draftReportData!.id,
          valuesForSubmit
        )

        await persistConsumption(draftReportData!.id)

        await updatedSubmittedReport(
          draftReportData!.id,
          selectedReport?.name ?? 'Unnamed Report',
          statusToSend,
          totalWarnings > 0,
          voyage?.id ?? 0,
          Number(assignmentId),
          valuesForSubmit,
          vesselIdNumber,
          totalWarnings,
          hasReview,
          reportRemark,
          undefined,
          undefined,
          undefined,
          undefined,
          legIdNumber
        )

        toast.success('Report submitted successfully.')
      } else {
        const reportSourceName = selectedReport?.name || 'Manual Report'
        const initialCreateStatus = shouldFinalizeSubmit ? ReportStatus.DRAFT : statusToSend

        const created: any = await submitReport(
          selectedReport?.name ?? 'Unnamed Report',
          initialCreateStatus,
          totalWarnings > 0,
          voyage?.id ?? 0,
          Number(assignmentId),
          finalValues,
          vesselIdNumber,
          totalWarnings,
          hasReview,
          reportRemark,
          undefined,
          undefined,
          undefined,
          undefined,
          legIdNumber
        )

        const createdId = extractReportId(created)
        if (!createdId) {
          toast.error(
            'Report saved but report id not returned from API. Update backend to return created report id.'
          )
          return
        }

        await persistConsumption(createdId)

        await persistBunkerAttachments(createdId)
        const valuesForSubmit = await buildBunkerAttachmentValuesWithUploadedIds(createdId, finalValues)

        if (shouldFinalizeSubmit) {
          await updatedSubmittedReport(
            createdId,
            selectedReport?.name ?? 'Unnamed Report',
            ReportStatus.SUBMITTED,
            totalWarnings > 0,
            voyage?.id ?? 0,
            Number(assignmentId),
            valuesForSubmit,
            vesselIdNumber,
            totalWarnings,
            hasReview,
            reportRemark,
            undefined,
            undefined,
            undefined,
            undefined,
            legIdNumber
          )
        } else if (isBunkerReport) {
          await updatedSubmittedReport(
            createdId,
            selectedReport?.name ?? 'Unnamed Report',
            statusToSend,
            totalWarnings > 0,
            voyage?.id ?? 0,
            Number(assignmentId),
            valuesForSubmit,
            vesselIdNumber,
            totalWarnings,
            hasReview,
            reportRemark,
            undefined,
            undefined,
            undefined,
            undefined,
            legIdNumber
          )
        }

        toast.success('Report submitted successfully.')
      }

      try {
        localStorage.removeItem(backupStorageKey)
      } catch (e) {}

      setTimeout(() => navigate('/operations/overview?tab=view2', {replace: true}), 300)
    } catch (err) {
      console.error('Failed to submit report:', err)
      toast.error(getApiErrorMessage(err, 'Failed to submit the report. Please try again.'))
      submitActionConsumedRef.current = false
      setIsSubmitActionConsumed(false)
    } finally {
      reportActionInFlightRef.current = false
      setIsReportActionInProgress(false)
    }
  }

  const handleSubmit = () => {
    if (reportActionInFlightRef.current || submitActionConsumedRef.current) return

    if (isSuperAdmin) {
      setModalActionType('submit')
      setIsConfirmOverWriteModalOpen(true)
      return
    }

    void executeSubmit()
  }

  const handleConfirmOverwriteSubmit = async () => {
    setIsConfirmOverWriteModalOpen(false)
    await executeSubmit()
  }

  useEffect(() => {
    if (!assignmentId) return

    const fetchAssignmentDetails = async () => {
      try {
        const assignment = await getAssignmentById(Number(assignmentId))
        if (!assignment) return
        assignmentDetailsCache.set(Number(assignmentId), assignment)

        const reportData = resolveAssignmentTemplate(assignment)
        if (!reportData || !reportData.menus) {
          setTabs([])
          return
        }

        const name =
          assignment.customTemplate?.menus?.[0]?.name ||
          assignment.template?.name ||
          'Unnamed Report'
        const flattenedSubmenus = reportData.menus.flatMap((menu: any) => menu.submenus)

        setSelectedReport({
          id: assignment.id,
          name,
          templateId: reportData.id,
          order: 0,
          isActive: true,
          submenus: flattenedSubmenus,
        })

        const activeSubmenus = reportData.menus
          .filter((menu: any) => menu.isActive)
          .flatMap((menu: any) => menu.submenus.filter((submenu: any) => submenu.isActive))

        setTabs(activeSubmenus)

        const vId = Number(
          assignment.template?.vessel?.id ||
          (assignment as any)?.vessel?.id ||
          (assignment as any)?.vesselId ||
          currentUser?.vessel?.id ||
          (currentUser as any)?.vesselId ||
          0
        )
        if (!selectedVoyage) {
          try {
            const voyageList = await getVoyageList()
            const matchVoyage =
              (Array.isArray(voyageList) ? voyageList : []).find(
                (v: any) => (vId > 0 ? Number(v?.vessel?.id) === vId : true) && v.active
              ) ||
              (Array.isArray(voyageList) ? voyageList : []).find(
                (v: any) => (vId > 0 ? Number(v?.vessel?.id) === vId : true)
              ) ||
              (Array.isArray(voyageList) ? voyageList : [])[0]
            if (matchVoyage) {
              setVoyage(matchVoyage)
            }
          } catch {}
        }

        if (draftReportData && (draftReportData as any).values) {
          const formDataFromReport = mapCreatedValuesToFormData(
            (draftReportData as any).values,
            reportData.menus
          )
          setFormData(formDataFromReport)
        }
      } catch (err) {
        console.error('Failed to fetch assignment details:', err)
      }
    }

    fetchAssignmentDetails()
  }, [assignmentId])

  // Load the previous report's Miscellaneous Consumables values (create mode only)
  // so they can be shown in a read-only "Previous consumption" column.
  useEffect(() => {
    const loadPreviousMiscConsumption = async () => {
      if (isEditModeActive) {
        setPreviousMiscConsumption({})
        return
      }
      const miscTab = tabs.find((t) => t.name === 'Miscellaneous Consumables')
      if (!miscTab) return

      const voyageId = Number(voyage?.id ?? 0)
      const vesselId = Number(voyage?.vessel?.id ?? 0)
      if (!(voyageId > 0) || !(vesselId > 0)) return

      const norm = (s: any) => String(s ?? '').trim().toLowerCase()
      const getReportVoyageId = (r: any): number => {
        if (!r) return NaN
        if (typeof r.voyage === 'number') return r.voyage
        if (typeof r.voyage?.id === 'number') return r.voyage.id
        if (typeof r.voyageDto?.id === 'number') return r.voyageDto.id
        if (typeof r.voyageId === 'number') return r.voyageId
        const raw = r.voyage?.id ?? r.voyageDto?.id ?? r.voyageId ?? r.voyage
        const parsed = Number(raw)
        return Number.isFinite(parsed) ? parsed : NaN
      }
      const getReportVesselId = (r: any): number => {
        if (!r) return NaN
        if (typeof r.vesselId === 'number') return r.vesselId
        if (typeof r.vessel?.id === 'number') return r.vessel.id
        if (typeof r.voyage?.vessel?.id === 'number') return r.voyage.vessel.id
        if (typeof r.voyageDto?.vessel?.id === 'number') return r.voyageDto.vessel.id
        if (typeof r.assignment?.vessel?.id === 'number') return r.assignment.vessel.id
        const raw =
          r.vesselId ??
          r.vessel?.id ??
          r.voyage?.vessel?.id ??
          r.voyageDto?.vessel?.id ??
          r.assignment?.vessel?.id ??
          r.vessel
        const parsed = Number(raw)
        return Number.isFinite(parsed) ? parsed : NaN
      }
      const toReportTime = (r: any) => {
        const raw = r?.reportDateTime ?? r?.submittedDateTime ?? r?.createdDateTime ?? ''
        const t = new Date(raw).getTime()
        return Number.isFinite(t) ? t : 0
      }

      try {
        const reports = await getCreatedReportById(vesselId)
        const sameVoyage = (Array.isArray(reports) ? reports : []).filter((r: any) => {
          const id = Number(r?.id)
          return (
            id > 0 && getReportVoyageId(r) === voyageId && getReportVesselId(r) === vesselId
          )
        })
        if (sameVoyage.length === 0) {
          setPreviousMiscConsumption({})
          return
        }

        const submitted = sameVoyage.filter(
          (r: any) => norm(r?.status) === 'submitted'
        )
        const ordered = (submitted.length > 0 ? submitted : sameVoyage).sort(
          (a: any, b: any) => toReportTime(b) - toReportTime(a)
        )
        const previousReport: any = ordered[0]
        if (!previousReport) {
          setPreviousMiscConsumption({})
          return
        }

        // Build the previous-consumption map keyed by field id (stable across
        // same-template reports). Value entries carry {field, valueText} and an
        // unreliable `label`, so field id is the primary key; label is a fallback.
        const map: Record<string, string> = {}
        for (const entry of previousReport?.values || []) {
          const val = String(entry?.valueText ?? entry?.value ?? '').trim()
          if (!val || val === '--') continue
          const rawF = entry?.field
          const fid = typeof rawF === 'object' && rawF !== null ? Number(rawF.id) : Number(rawF)
          if (Number.isFinite(fid) && fid > 0) map[`id:${fid}`] = val
          const lbl = norm(entry?.label || (typeof rawF === 'object' && rawF?.label))
          if (lbl) map[`label:${lbl}`] = val
        }
        setPreviousMiscConsumption(map)
      } catch (err) {
        console.error('Failed to load previous Miscellaneous Consumables:', err)
        setPreviousMiscConsumption({})
      }
    }

    loadPreviousMiscConsumption()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditModeActive, tabs, voyage?.id, voyage?.vessel?.id])

  const fetchFieldsforStep = async (tabId: number) => {
    try {
      setIsLoadingFields(true)
      const allSubmenus = selectedReport?.submenus ?? []
      const targetTab = allSubmenus?.find((submenu) => submenu.id === tabId)

      if (targetTab) {
        let activeFields = sortFieldsByOrder(
          (targetTab.fields || []).filter((field) => field.isActive)
        )
        if (isFuelChangeoverReport || /change\s*over/i.test(targetTab.name)) {
          activeFields = ensureFuelChangeoverPresetFields(targetTab.name, activeFields)
        }
        setFields(activeFields)
      } else {
        setFields([])
      }
    } catch (err) {
      console.error('Failed to fetch fields list:', err)
      setFields([])
    } finally {
      setIsLoadingFields(false)
    }
  }

  const nextStep = () => {
    const currentTabName = tabs[currentStep]?.name
    const skipValidation = currentTabName === 'Consumption & ROB' || isBunkerReport

    const isValid = skipValidation ? true : Boolean(stepRefs.current[currentStep]?.validateStep())
    if (!isValid) return

    setStepCompletionStatus((prev) => {
      const updated = [...prev]
      updated[currentStep] = true
      return updated
    })

    if (isFormDirtyRef.current) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current)
      }
      performAutoSaveDraftRef.current?.(formData)
    }

    setCurrentStep((prev) => Math.min(prev + 1, tabs.length - 1))
  }

  const isStepCompleted = (index: number) => stepCompletionStatus[index] === true
  const goToStep = (step: number) => {
    if (isFormDirtyRef.current) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current)
      }
      performAutoSaveDraftRef.current?.(formData)
    }
    setCurrentStep(step)
  }
  const normalizeStepName = (name: string) => String(name || '').replace(/^\s*\d+\.\s*/, '').trim()
  const getStepDisplayName = (name: string) =>
    isBunkerReport ? normalizeStepName(name) : String(name || '')

  useEffect(() => {
    if (step?.name === 'Consumption & ROB') {
      setConsumptionRefreshFlag((prev) => prev + 1)
    }
  }, [step?.id, step?.name])
  if (!isEditModeActive && !draftReportData && isCheckingActiveDraft) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{height: '70vh'}}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Checking draft status...</span>
        </div>
      </div>
    )
  }

  if (!tabs.length || !tabs[currentStep]) {
    return <div className='p-5 fw-500 text-center'>No tabs available for this report</div>
  }

  return (
    <>
      <style>{shimmerKeyframes}</style>
      <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
        <div className='d-flex flex-column flex-column-fluid border-top' style={{height: '90vh'}}>
          <div
            id='kt_app_content'
            className='app-content flex-column-fluid d-flex flex-column bg-white'
            style={{flex: 1}}
          >
            <div className='card d-flex flex-column bg-white' style={{flex: 1}}>
              <div className='card-header align-items-center justify-content-between py-4 gap-2 gap-md-12 px-5 bg-white'>
                <div>
                  <div className='d-flex align-items-center gap-1 mb-4'>
                    <Link to='' onClick={() => setIsCloseReportModalOpen(true)}>
                      <KTSVG
                        path='/media/icons/duotune/arrows/ArrowLeft.svg'
                        className='svg-icon-2'
                      />
                    </Link>
                    <h3
                      className='card-title fw-bold text-dark m-0 p-0'
                      style={{fontSize: '1.5rem'}}
                    >
                      {reportHeaderTitle}
                    </h3>
                  </div>
                  <div className='d-flex gap-12'>
                    <p>
                      Vessel name:{' '}
                      <strong>
                        <VesselLandingLink
                          vesselId={voyage?.vessel?.id ?? null}
                          label={voyage?.vessel?.fleet_name}
                          className='text-dark text-decoration-underline'
                        />
                      </strong>
                    </p>
                    <p>
                      IMO number: <strong>{voyage?.vessel?.imoNumber}</strong>
                    </p>
                    <p>
                      Vessel type: <strong>{voyage?.vessel?.vesselType}</strong>
                    </p>
                  </div>
                </div>

                <div className='mb-4 text-end'>
                  <div className='mb-2'>
                    <strong style={{fontSize: '1.5rem'}}>{selectedReport?.name}</strong>{' '}
                    {formatReportDateTimeDisplay(reportDate)}
                  </div>
                  <div className='d-flex align-items-center justify-content-end gap-3'>
                    {/* ── Auto-Save Real-Time & Local Backup Status Badge ── */}
                    <div className='d-flex align-items-center gap-2'>
                      {autoSaveStatus === 'SAVING' && (
                        <span className='badge badge-light-primary d-flex align-items-center gap-1 py-2 px-3 fs-8'>
                          <span
                            className='spinner-border spinner-border-sm text-primary me-1'
                            style={{width: '0.85rem', height: '0.85rem'}}
                          />
                          <span className='fw-bold text-primary'>Auto-saving draft...</span>
                        </span>
                      )}
                      {autoSaveStatus === 'SAVED' && (
                        <span className='badge badge-light-success d-flex align-items-center gap-1 py-2 px-3 fs-8'>
                          <i className='bi bi-check-circle-fill text-success fs-7'></i>
                          <span className='fw-bold text-success'>Draft saved</span>
                          {lastAutoSavedTime && (
                            <span className='text-muted fs-9 ms-1'>
                              {lastAutoSavedTime.toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                              })}
                            </span>
                          )}
                        </span>
                      )}
                      {autoSaveStatus === 'OFFLINE' && (
                        <span
                          className='badge badge-light-warning d-flex align-items-center gap-1 py-2 px-3 fs-8'
                          title='Draft backed up locally. Satellite disconnect / crash safe.'
                        >
                          <i className='bi bi-shield-check text-warning fs-7'></i>
                          <span className='fw-bold text-warning'>Local Backup Active</span>
                        </span>
                      )}
                      {autoSaveStatus === 'IDLE' && (
                        <span
                          className='badge badge-light-secondary d-flex align-items-center gap-1 py-2 px-3 fs-8'
                          title='Auto-save active on every change'
                        >
                          <i className='bi bi-cloud-check text-muted fs-7'></i>
                          <span className='text-muted'>Auto-save active</span>
                        </span>
                      )}
                    </div>

                    {reportActionState.isLocked && (
                      <span className='badge badge-light-success d-flex align-items-center gap-1 py-2 px-3 fs-7 fw-bold'>
                        <i className='bi bi-lock-fill text-success fs-6 me-1'></i>
                        Submitted &amp; Locked
                      </span>
                    )}

                    {reportActionState.canSaveDraft && (
                      <button
                        type='button'
                        onClick={handleSaveDraft}
                        className='btn btn-sm btn-light-primary fw-bold d-flex align-items-center gap-1'
                        disabled={isReportActionInProgress}
                      >
                        {isReportActionInProgress ? 'Saving...' : topActionLabel}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {draftReportData?.remark && (
                <div className='d-flex justify-content-between align-items-center py-4 px-5 border-bottom'>
                  <div className='text-muted'>{draftReportData.remark}</div>
                </div>
              )}

              {pendingLocalDraft && (
                <div className='mx-5 my-3 p-4 rounded bg-light-warning border border-warning d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 shadow-sm'>
                  <div className='d-flex align-items-center gap-3'>
                    <div
                      className='p-2 rounded-circle d-flex align-items-center justify-content-center'
                      style={{background: 'rgba(255, 199, 0, 0.2)'}}
                    >
                      <i className='bi bi-clock-history fs-2 text-warning'></i>
                    </div>
                    <div>
                      <div className='fw-bolder text-gray-900 fs-6'>
                        Unsubmitted Local Draft Found
                      </div>
                      <div className='text-gray-600 fs-7'>
                        Saved on{' '}
                        <span className='fw-semibold text-gray-800'>
                          {new Date(pendingLocalDraft.savedAt).toLocaleString()}
                        </span>
                        . Would you like to restore your unsubmitted work or start fresh?
                      </div>
                    </div>
                  </div>
                  <div className='d-flex align-items-center gap-2 flex-shrink-0'>
                    <button
                      type='button'
                      className='btn btn-sm btn-light-danger fw-bold'
                      onClick={handleDiscardLocalDraft}
                    >
                      Discard &amp; Start Fresh
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-primary fw-bold d-flex align-items-center gap-1'
                      onClick={handleRestoreLocalDraft}
                    >
                      <i className='bi bi-arrow-counterclockwise fs-7'></i>
                      Restore Draft
                    </button>
                  </div>
                </div>
              )}

              <div className='d-flex px-5'>
                <div
                  className='pe-3 border-end py-3'
                  style={{flex: 0.82, overflowY: 'auto', maxHeight: 'calc(100vh - 240px)'}}
                >
                  <div className='p-3 list-group-item' style={{backgroundColor: '#F4F9FF'}}>
                    <div className='title pb-1'>
                      {voyage?.voyageNumber || 'VM009'}: {voyage?.departurePort || 'Mumbai'} -{' '}
                      {voyage?.arrivalPort || selectedVoyage?.arrivalPort || 'Singapore'}
                    </div>
                    {voyage?.legs && voyage.legs.length > 0 && (() => {
                      const leg =
                        voyage.legs.find((l: any) => l.id === selectedLegId || (l.id === draftReportData?.legId)) ||
                        voyage.legs.find((l: any) => l.active) ||
                        voyage.legs[0]
                      return (
                        <div className='my-1'>
                          <span className='badge badge-light-info text-info fw-semibold' style={{fontSize: '11px'}}>
                            Leg {leg.sequenceNumber}: {leg.departurePort} → {leg.arrivalPort || 'TBD'}
                          </span>
                        </div>
                      )
                    })()}
                    {voyage?.startDate && voyage?.endDate && (
                      <div className='details pb-1'>
                        {new Date(voyage?.startDate).toLocaleDateString('en-GB')} -{' '}
                        {new Date(voyage?.endDate).toLocaleDateString('en-GB')}
                      </div>
                    )}
                    <div className='d-flex justify-content-between py-4 pe-2 gap-1'>
                      <span
                        className='badge'
                        style={{
                          backgroundColor: '#FDF6B2',
                          fontSize: '13px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontWeight: 500,
                        }}
                      >
                        Current
                      </span>
                      {hasWarnings && (
                        <div className='d-flex gap-1 align-items-center'>
                          <KTSVG path='/media/map/Incomplete.svg' className='svg-icon-3' />
                          <span className='text-muted'>{totalWarnings}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {tabs.map((s, index) => (
                    <div
                      key={s.id}
                      className={`d-flex justify-content-between mt-5 py-5 px-5 border rounded-1 fw-bold
                    ${
                      index === currentStep
                        ? 'current'
                        : isStepCompleted(index)
                        ? 'completed-step'
                        : 'form-step'
                    }`}
                      onClick={() => {
                        goToStep(index)
                        fetchFieldsforStep(s.id)
                      }}
                      style={{cursor: 'pointer'}}
                    >
                      <div className='d-flex align-items-center gap-2'>
                        <span className='fs-5'>
                          {isBunkerReport ? getStepDisplayName(s.name) : `${index + 1}. ${getStepDisplayName(s.name)}`}
                        </span>
                        {hasErrorsByStep[s.id] && (
                          <span
                            className='badge bg-danger text-white'
                            style={{fontSize: '11px', padding: '2px 6px', borderRadius: '4px'}}
                          >
                            {errorsByStep[s.id]} error{errorsByStep[s.id] > 1 ? 's' : ''}
                          </span>
                        )}
                        {!hasErrorsByStep[s.id] && hasWarningsByStep[s.id] && (
                          <span
                            className='badge bg-warning text-dark'
                            style={{fontSize: '11px', padding: '2px 6px', borderRadius: '4px'}}
                          >
                            {warningsByStep[s.id]} warning{warningsByStep[s.id] > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {index === currentStep ? (
                        <KTSVG path='/media/map/arrow-right.svg' className='svg-icon svg-icon-2' />
                      ) : isStepCompleted(index) ? (
                        <KTSVG
                          path='/media/map/check.svg'
                          className='svg-icon svg-icon-2 text-success'
                        />
                      ) : null}
                    </div>
                  ))}
                </div>

                <div className='py-3 px-5' style={{flex: 3}}>
                  {isLoadingFields ? (
                    renderFieldLoader()
                  ) : step.name === 'Consumption & ROB' ? (
                  <AddConsumptionROB
                    ref={consumptionRobRef}
                    sectionTitle={step.name}
                      voyage={voyage}
                      reportId={activeReportId}
                      legId={resolvedLegId}
                      fields={fields}
                      refreshFlag={consumptionRefreshFlag}
                      snapshot={consumptionPreviewData}
                      isFuelChangeover={isFuelChangeoverReport}
                      reportName={selectedReport?.name ?? draftReportData?.reportType ?? ''}
                      onDtoChange={(dto, isUserInitiated = false) => {
                        if (isMeaningfulConsumptionDto(dto)) {
                          setConsumptionPreviewData(dto)
                          if (isUserInitiated) {
                            isFormDirtyRef.current = true
                          }
                          try {
                            localStorage.setItem(
                              backupStorageKey,
                              JSON.stringify({
                                formData,
                                consumptionPreviewData: dto,
                                activeReportId: activeReportIdRef.current,
                                savedAt: new Date().toISOString(),
                              })
                            )
                          } catch (e) {
                            console.warn('Local backup write warning:', e)
                          }
                          if (isFormDirtyRef.current) {
                            if (autoSaveTimerRef.current) {
                              clearTimeout(autoSaveTimerRef.current)
                            }
                            setAutoSaveStatus('SAVING')
                            autoSaveTimerRef.current = setTimeout(() => {
                              performAutoSaveDraftRef.current?.(formData, dto)
                            }, 1500)
                          }
                        }
                      }}
                      values={(formData as any)[step.id] || {}}
                      onChange={(label, id, value, isUserInitiated = false) => {
                        handleFieldChange(step.id, label, id.toString(), value, isUserInitiated)
                        setStepCompletionStatus((prev) => {
                          const updated = [...prev]
                          updated[currentStep] = true
                          return updated
                        })
                      }}
                    />
                  ) : isBunkerReport ? (
                    <BunkerReportStep
                      formSections={fields}
                      lookupSections={tabs.flatMap((t) => t.fields || [])}
                      values={(formData as any)[step.id] || EMPTY_TAB_VALUES}
                      allValues={formData as any}
                      reportId={activeReportId}
                      initialPendingAttachments={pendingBunkerAttachments}
                      onPendingAttachmentsChange={setPendingBunkerAttachments}
                      onChange={(label, id, value, isUserInitiated = true) => {
                        const targetTabId = resolveTabIdByFieldId(id, step.id)
                        handleFieldChange(targetTabId, label, id, value, isUserInitiated)
                        setStepCompletionStatus((prev) => {
                          if (prev[currentStep]) return prev
                          const updated = [...prev]
                          updated[currentStep] = true
                          return updated
                        })
                      }}
                    />
                  ) : (
                    <ReportStep
                      ref={(ref) => (stepRefs.current[currentStep] = ref!)}
                      sectionTitle={step.name}
                      tabularLayout={step.name === 'Miscellaneous Consumables'}
                      showPreviousColumn={
                        !isEditModeActive && step.name === 'Miscellaneous Consumables'
                      }
                      previousValues={previousMiscConsumption}
                      autofillVoyageFields={!isEditModeActive}
                      formSections={fields}
                      values={(formData as any)[step.id] || {}}
                      voyage={voyage}
                      selectedReport={selectedReport}
                      onChange={(label, id, value, isUserInitiated = true) => {
                        handleFieldChange(step.id, label, id, value, isUserInitiated)
                        const isValid = stepRefs.current[currentStep]?.validateStep?.()
                        setStepCompletionStatus((prev) => {
                          const updated = [...prev]
                          updated[currentStep] = !!isValid
                          return updated
                        })
                      }}
                    />
                  )}

                  {currentStep < tabs.length - 1 && (
                    <button className='btn btn_primary mt-4' onClick={nextStep}>
                      Next: {getStepDisplayName(tabs[currentStep + 1].name)}{' '}
                      <FaChevronRight className='ms-2' />
                    </button>
                  )}

                  {currentStep === tabs.length - 1 && (
                    <div className='d-flex align-items-center gap-3 mt-5 flex-wrap'>
                      {reportActionState.canSubmit && (
                        <button
                          type='button'
                          className='btn btn_primary d-flex align-items-center gap-1 fw-bold'
                          onClick={handleSubmit}
                          disabled={isReportActionInProgress || isSubmitActionConsumed}
                        >
                          <i className='bi bi-lock-fill me-1'></i>
                          {isReportActionInProgress || isSubmitActionConsumed
                            ? 'Please wait...'
                            : 'Submit & Lock'}
                        </button>
                      )}
                      {!reportActionState.canSubmit &&
                        reportActionState.submitTooltip &&
                        !reportActionState.isLocked && (
                          <div className='d-flex align-items-center gap-2 text-muted fs-7 bg-light-info p-2 rounded border border-info'>
                            <i className='bi bi-info-circle text-info fs-6'></i>
                            <span>{reportActionState.submitTooltip}</span>
                          </div>
                        )}
                      {reportActionState.isLocked && (
                        <span className='badge badge-light-success d-flex align-items-center gap-1 py-2 px-3 fs-7 fw-bold'>
                          <i className='bi bi-lock-fill text-success fs-6 me-1'></i>
                          Submitted &amp; Locked
                        </span>
                      )}
                      {reportDate && opCan(OP.READ_POSITION_REPORT) && (
                        <Link
                          to='/operations/report-preview'
                          state={{
                            formData,
                            tabs,
                            pendingBunkerAttachments,
                            assignmentId,
                            selectedVoyage,
                            stepCompletionStatus,
                            selectedReport,
                            reportDate,
                            reportId: activeReportId ?? draftReportData?.id,
                            consumptionPreviewData:
                              consumptionRobRef.current?.getDto?.() || consumptionPreviewData || null,
                            vesselId: voyage?.vessel?.id ?? currentUser?.vessel?.id,
                          }}
                          className='btn p-0 ps-3'
                        >
                          Preview report
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {isCloseReportModalOpen && (
                <CloseReportModal
                  isOpen={isCloseReportModalOpen}
                  onClose={() => setIsCloseReportModalOpen(false)}
                  formData={formData}
                  assignmentId={Number(assignmentId)}
                  selectedReport={selectedReport}
                  selectedVoyage={voyage ? voyage : null}
                  onSaveDraft={handleSaveDraft}
                  onDiscard={handleDiscardLocalDraft}
                />
              )}

              {isConfirmOverWriteModalOpen && (
                <ConfirmOverwriteModal
                  isOpen={isConfirmOverWriteModalOpen}
                  onClose={() => setIsConfirmOverWriteModalOpen(false)}
                  formData={formData}
                  assignmentId={Number(assignmentId)}
                  selectedReport={selectedReport}
                  selectedVoyage={voyage ? voyage : null}
                  onSaveDraft={handleConfirmOverwriteSave}
                  onSubmitReport={handleConfirmOverwriteSubmit}
                  actionType={modalActionType}
                />
              )}

              <ToastContainer
                position='top-center'
                autoClose={3000}
                newestOnTop
                closeOnClick
                pauseOnHover
                style={{top: '5rem'}}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export {CreateReport}
